/** Логика предпросмотра фиксации (не компоненты): что строка позволяет, какая спецификация
 *  пишется, фильтр по флагам и поиску, галочки «Фиксировать», сбор тела `confirm`. Повторяет
 *  правила `service.confirm_fixation` / `flags.derive_row`, чтобы экран обещал ровно то, что
 *  запишет бэкенд. */
import { SPEC_NONE, type ConfirmIn, type PreviewFlag, type PreviewRow, type PreviewSpec } from "@/api/purchasePrices";
import { jumpPct } from "./format";

/** Что можно сделать со строкой при подтверждении. */
export type RowMode =
  /** пишется, пока стоит галочка «Фиксировать» */
  | { kind: "writable" }
  /** посчитано по нескольким рецептурам — пишется только с явным выбором (или снять галочку) */
  | { kind: "needs_spec" }
  /** не пишется ни при каком выборе — галочка недоступна, причина рядом и в подсказке */
  | { kind: "locked"; reason: string };

export function rowMode(row: PreviewRow): RowMode {
  if (row.flag === "kept_manual") {
    return { kind: "locked", reason: "действует цена вручную/контракт — фиксация её не трогает" };
  }
  if (row.flag === "exists_same_date") {
    return { kind: "locked", reason: "на эту дату уже есть цена — менять через «Исправить»" };
  }
  if (row.flag === "missing_in_calc") {
    return { kind: "locked", reason: "нет в расчёте производства — цена не меняется" };
  }
  if (row.flag === "not_computed") {
    return { kind: "locked", reason: "цена не посчитана — не пишется" };
  }
  if (row.flag === "multi_spec") return { kind: "needs_spec" };
  return { kind: "writable" };
}

/** Посчитанные (`ok`) спецификации строки — только из них пишется цена. */
export function okSpecs(row: PreviewRow): PreviewSpec[] {
  return row.specs.filter((s) => s.ok);
}

/** Значение выбора спецификации: её `spec_ref`, у спецификации без него — `SPEC_NONE`. */
export function specChoiceValue(spec: PreviewSpec): string {
  return spec.spec_ref ?? SPEC_NONE;
}

/** Выбор человека: строки со снятой галочкой «Фиксировать» и рецептура для `multi_spec`. */
export interface Selection {
  /** снятые галочки (по умолчанию стоят у всех доступных строк) */
  excluded: ReadonlySet<string>;
  specChoice: Readonly<Record<string, string>>;
}

export const EMPTY_SELECTION: Selection = { excluded: new Set(), specChoice: {} };

/** Спецификация, по которой пойдёт цена: у `multi_spec` — выбранная, иначе — автоспецификация
 *  бэка (`auto_spec`: единственная посчитанная или несколько с равной ценой); нет — null. */
export function chosenSpec(row: PreviewRow, sel: Selection): PreviewSpec | null {
  const ok = okSpecs(row);
  if (row.flag === "multi_spec") {
    const ref = sel.specChoice[row.key];
    if (ref === undefined) return null;
    return ok.find((s) => specChoiceValue(s) === ref) ?? null;
  }
  if (row.auto_spec === null) return null;
  return ok.find((s) => specChoiceValue(s) === row.auto_spec) ?? null;
}

/** Новая цена с НДС строки (по пишущейся спецификации); не выбрана / не посчитано — null. */
export function newPrice(row: PreviewRow, sel: Selection): number | null {
  return chosenSpec(row, sel)?.price_gross ?? null;
}

/** Скачок %: у `multi_spec` — по выбранной рецептуре тем же правилом здесь, иначе — от бэка. */
export function rowJump(row: PreviewRow, sel: Selection): number | null {
  if (row.flag === "multi_spec") return jumpPct(newPrice(row, sel), row.current?.price_gross);
  return row.jump_pct;
}

/** Галочка «Фиксировать» стоит (у недоступных строк — никогда). */
export function isChecked(row: PreviewRow, sel: Selection): boolean {
  return rowMode(row).kind !== "locked" && !sel.excluded.has(row.key);
}

/** Строка будет записана при подтверждении с этим выбором. */
export function willWrite(row: PreviewRow, sel: Selection): boolean {
  if (!isChecked(row, sel)) return false;
  if (rowMode(row).kind === "needs_spec") return chosenSpec(row, sel) !== null;
  return true;
}

export interface ConfirmPlan {
  body: ConfirmIn;
  /** сколько доступных строк отмечено «Фиксировать» (без блокировки — ровно столько запишется) */
  checked: number;
  /** отмеченные `multi_spec` без выбранной рецептуры — подтверждать нельзя */
  missingSpec: string[];
}

/** Тело `confirm`: `exclude` — доступные строки со снятой галочкой (у недоступных смысла нет),
 *  `spec_choice` — только отмеченные `multi_spec` с выбранной рецептурой. */
export function confirmPlan(rows: PreviewRow[], sel: Selection): ConfirmPlan {
  const exclude: string[] = [];
  const spec_choice: Record<string, string> = {};
  const missingSpec: string[] = [];
  let checked = 0;
  for (const row of rows) {
    const mode = rowMode(row);
    if (mode.kind === "locked") continue;
    if (sel.excluded.has(row.key)) {
      exclude.push(row.key);
      continue;
    }
    checked += 1;
    if (mode.kind === "needs_spec") {
      const spec = chosenSpec(row, sel);
      if (spec === null) missingSpec.push(row.key);
      else spec_choice[row.key] = specChoiceValue(spec);
    }
  }
  return { body: { exclude, spec_choice }, checked, missingSpec };
}

/** Состояние галочки в шапке по ВИДИМЫМ строкам; доступных среди них нет — `none_available`. */
export type HeaderState = "all" | "some" | "none" | "none_available";

export function headerState(visible: PreviewRow[], sel: Selection): HeaderState {
  const available = visible.filter((r) => rowMode(r).kind !== "locked");
  if (available.length === 0) return "none_available";
  const n = available.filter((r) => !sel.excluded.has(r.key)).length;
  if (n === available.length) return "all";
  return n === 0 ? "none" : "some";
}

/** Отметить / снять «Фиксировать» у всех доступных ВИДИМЫХ строк (после фильтра и поиска);
 *  остальные строки не трогаются. */
export function setVisibleChecked(visible: PreviewRow[], sel: Selection, checked: boolean): Selection {
  const excluded = new Set(sel.excluded);
  for (const r of visible) {
    if (rowMode(r).kind === "locked") continue;
    if (checked) excluded.delete(r.key);
    else excluded.add(r.key);
  }
  return { ...sel, excluded };
}

/** Фильтр таблицы предпросмотра: флаг (null — все) и подстрока артикула/названия/ключа. */
export function filterRows(rows: PreviewRow[], flag: PreviewFlag | null, q: string): PreviewRow[] {
  const needle = q.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (flag === null || r.flag === flag) &&
      (!needle ||
        [r.article, r.name, r.key].some((v) => (v ?? "").toLowerCase().includes(needle))),
  );
}

/** Снять «Фиксировать» у всех строк, которым нужна рецептура, а её не выбрали. */
export function excludeMissingSpec(rows: PreviewRow[], sel: Selection): Selection {
  const { missingSpec } = confirmPlan(rows, sel);
  return { ...sel, excluded: new Set([...sel.excluded, ...missingSpec]) };
}

/** Причины «не посчитано» — `issues` всех спецификаций, без повторов. */
export function notComputedReasons(row: PreviewRow): string[] {
  return [...new Set(row.specs.flatMap((s) => s.issues))];
}

/** Подсказка у строки «не посчитано»: что делать человеку. */
export const NOT_COMPUTED_HINT =
  "Цена не посчитана — внесите вручную («Добавить цену» / xlsx) или дозаполните данные на портале " +
  "производства (например, матрицу скоростей фасовки) и запустите фиксацию заново";
